import type { Component, PageTemplate, PageTemplateComponent } from '../../../../api/pages-client/pages-client.type';
import { ObjectType } from './dashboard-template.type';

/**
 * Converts a page template into a list of Components by mapping object keys to actual object UUIDs
 *
 * @param {PageTemplate | null | undefined} pageTemplate - The page template to convert
 * @param {Record<string, string>} objectKeyToUuidMap - Dictionary mapping object keys to actual object UUIDs
 * @param {string} pageId - The ID of the page these components will belong to
 * @returns {Component[]} Array of Components with object_ids mapped to actual UUIDs
 */
function convertPageTemplateToComponents(
  pageTemplate: PageTemplate | null | undefined,
  objectKeyToUuidMap: Record<string, string>,
  pageId: string,
): Component[] {
  if (!pageTemplate?.config?.components) {
    return [];
  }

  return pageTemplate.config.components.map((templateComponent: PageTemplateComponent): Component => {
    // Convert PageTemplateComponent to Component by adding the page field
    const component: Component = {
      ...templateComponent,
      page: pageId,
    };

    // Map object_ids from keys to actual UUIDs in component_data
    if (component.component_data) {
      component.component_data = component.component_data.map((data) => {
        // Map object_ids from template keys to actual UUIDs
        const mappedObjectIds = data.object_ids.map((objectKey) => {
          const actualUuid = objectKeyToUuidMap[objectKey];
          if (!actualUuid) {
            return objectKey; // Return original key if no mapping found
          }
          return actualUuid;
        });

        // Update color_map to use actual UUIDs instead of keys
        let mappedColorMap = data.color_map;
        if (data.color_map) {
          mappedColorMap = {};
          for (const [colorKey, colorValue] of Object.entries(data.color_map)) {
            const actualUuid = objectKeyToUuidMap[colorKey];
            if (actualUuid) {
              mappedColorMap[actualUuid] = colorValue;
            } else {
              // If no mapping found, keep the original key but warn
              mappedColorMap[colorKey] = colorValue;
            }
          }
        }

        return {
          ...data,
          object_ids: mappedObjectIds,
          color_map: mappedColorMap,
        };
      });
    }

    return component;
  });
}

/**
 * Validates that all required object keys have mappings in the provided dictionary
 *
 * @param {PageTemplate | null | undefined} pageTemplate - The page template to validate
 * @param {Record<string, string>} objectKeyToUuidMap - Dictionary mapping object keys to actual object UUIDs
 * @returns {Object} Object with validation result and any missing keys
 */
function validateTemplateObjectMappings(
  pageTemplate: PageTemplate | null | undefined,
  objectKeyToUuidMap: Record<string, string>,
): { isValid: boolean; missingKeys: string[] } {
  const missingKeys: string[] = [];

  if (!pageTemplate?.config?.components) {
    return { isValid: true, missingKeys: [] };
  }

  // Collect all object keys used in the template
  const allObjectKeys = new Set<string>();

  pageTemplate.config.components.forEach((component) => {
    component.component_data?.forEach((data) => {
      data.object_ids.forEach((objectKey) => {
        allObjectKeys.add(objectKey);
      });
    });
  });

  // Check if all keys have mappings
  allObjectKeys.forEach((key) => {
    if (!objectKeyToUuidMap[key]) {
      missingKeys.push(key);
    }
  });

  return {
    isValid: missingKeys.length === 0,
    missingKeys,
  };
}

/**
 * Extracts all unique object keys used in a page template
 * This is useful for determining what mappings are needed
 *
 * @param {PageTemplate | null | undefined} pageTemplate - The page template to analyze
 * @returns {string[]} Array of unique object keys used in the template
 */
function extractTemplateObjectKeys(pageTemplate: PageTemplate | null | undefined): string[] {
  if (!pageTemplate?.config?.components) {
    return [];
  }

  const objectKeys = new Set<string>();

  for (const component of pageTemplate.config.components) {
    if (!component.component_data) continue;

    for (const data of component.component_data) {
      // Add all object IDs at once
      if (data.object_ids) {
        data.object_ids.forEach((key) => objectKeys.add(key));
      }
    }
  }

  return Array.from(objectKeys).sort();
}

/**
 * Converts an object type to a human readable string
 *
 * @param {string} object_type - The object type to convert
 * @returns {string} The human readable string
 */
function getTemplateObjectType(object_type: ObjectType): string {
  if (object_type === ObjectType.Sprint) {
    return 'Board';
  }
  return object_type.charAt(0).toUpperCase() + object_type.slice(1);
}

export {
  convertPageTemplateToComponents,
  extractTemplateObjectKeys,
  getTemplateObjectType,
  validateTemplateObjectMappings,
};
